// The captain picker card (mockup round 091 cards 01 and 03, R1893;
// ui-map-interface.md §12 "FLEET · MISSIONS").
//
// The captain choice of a docked warship is one card of the kit in the
// context slot, and a page of the menu sheet on a telephone: the title
// "Captain for <hull>", the kind `<CLASS> · n FREE ASHORE`, one captain
// row of `caprow.ts` per captain the hull can take, and one dark press.
//
// One rule holds for this card and for the start sheet of a dive: a press
// on a row selects the captain, and the one dark press names the selected
// captain (ASSIGN <NAME>, SEND <NAME>). The card opens with a captain
// selected, so one press still gives the order, and the press states who
// goes before it acts.
//
// The column of the attribute that leads the hull class is lit, in the
// strip of every row and in the column head (R1900). The catalog names the
// lead on the ship spec (`lead_attr`), and the client keeps no class table.
//
// The module is pure: it reads the state and writes markup.

import type { Captain, ShipSpec } from '../../gen/proto/aquaphobia';
import { overlay as S } from '../../gen/strings';
import type { Action, CardSpec } from '../cards/card';
import { shipMarkSvg } from '../cards/marks';
import { esc } from '../esc';
import type { SceneLook } from '../../render/scene-look';
import type { GameState } from '../../state';
import { captainHeadHtml, captainRowHtml, type CapRowCtx } from './caprow';
import { outfitJob } from './markup';

/**
 * The captains who can board a hull or lead a dive now: on the roster, in
 * no fleet, not wounded, not diving, not a prisoner and not outfitting for
 * another dive. The picker and the start sheet read the one gate (R421);
 * the server refuses anything it should (R84).
 */
export function freeCaptains(s: GameState): Captain[] {
  return s.roster.filter((c) => !c.fleetId && !c.injuredSecs && !c.expedition
    && !c.capturedBy
    && !outfitJob(s, c.id));
}

/** One row of a picker list: the captain and the lines the picker states
 *  in place of the row's own (markup, already escaped). */
interface PickRow {
  c: Captain;
  line2?: string;
  line3?: string;
}

/**
 * The list of a picker: the column head of the strip, then one captain row
 * per captain. Each row is one press that selects the captain (`act` with
 * the captain id), and the selected row is lit.
 */
export function pickListHtml(ctx: CapRowCtx, rows: readonly PickRow[],
  o: { lit?: number; sel: number; act: string }): string {
  if (rows.length === 0) return '';
  return '<div class="ck-pick">' + captainHeadHtml({ lit: o.lit, names: ctx.state.catalog?.attrs, open: ctx.codesOpen })
    + rows.map((r) => captainRowHtml(ctx, r.c, {
      lit: o.lit, line2: r.line2, line3: r.line3, selected: r.c.id === o.sel,
      pick: { act: o.act, id: r.c.id },
    })).join('')
    + '</div>';
}

/** The column the picker lights for a hull class (R1900): the catalog's
 *  lead attribute, or none for a class that names none (-1). */
export function leadAttr(ship: ShipSpec | undefined): number | undefined {
  return ship && ship.leadAttr >= 0 ? ship.leadAttr : undefined;
}

/**
 * The picker card for one hull in the pens, or null when the hull left
 * the pens. The officer aboard stands first in the list, and while that
 * officer is selected the press reads ABOARD and gives no order; BRING
 * ASHORE takes the officer off. A support hull carries no captain (R385),
 * so its card states that and lists nobody. The orders are the `setcap`
 * fleet order of today, so the server rule is unchanged. The lead column
 * of the class is lit (R1900); a class that names none lights nothing.
 */
export function pickerSpec(ctx: CapRowCtx, fleetId: number, sel: number, look?: SceneLook): CardSpec | null {
  const s = ctx.state;
  const st = s.pennedHull(fleetId);
  if (!st) return null;
  const ship = s.ship(st.cls);
  const free = freeCaptains(s);
  const aboard = st.captainId ? s.captain(st.captainId) : undefined;
  const base: CardSpec = {
    size: 'card', mark: shipMarkSvg(st.cls, look), title: S.pick_title({ hull: st.name }),
    ink: { faction: s.faction },
    kind: S.pick_kind({ cls: (ship?.name ?? '?').toUpperCase(), n: free.length }),
    close: true, closeAct: 'pickclose',
  };
  if (ship?.support) return { ...base, note: { lines: [esc(S.cap_support_none)] } };
  const choices = (aboard ? [aboard] : []).concat(free.filter((c) => c.id !== aboard?.id));
  if (choices.length === 0) return { ...base, note: { lines: [esc(S.cap_nobody)], tone: 'bad' } };
  const pick = choices.find((c) => c.id === sel) ?? free[0] ?? choices[0];
  const actions: Action[] = [pick.id === aboard?.id
    ? { act: 'setcap', id: fleetId, n: pick.id, face: 'dark', label: S.btn_aboard, off: true }
    : { act: 'setcap', id: fleetId, n: pick.id, face: 'dark', label: S.btn_assign_named({ name: pick.name.toUpperCase() }) }];
  if (aboard) actions.push({ act: 'setcap', id: fleetId, n: 0, face: 'quiet', label: S.btn_bring_ashore });
  return {
    ...base,
    body: pickListHtml(ctx, choices.map((c) => ({ c })), { lit: leadAttr(ship), sel: pick.id, act: 'picksel' }),
    actions,
  };
}
